import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useAtlasViewport } from '../DocsArea/Atlas/useAtlasViewport';
import { useAtlasNodeMotion } from '../DocsArea/Atlas/useAtlasNodeMotion';
import { useAtlasMarquee } from '../DocsArea/Atlas/hooks/useAtlasMarquee';
import { useAtlasCanvasEvents } from '../DocsArea/Atlas/hooks/useAtlasCanvasEvents';
import { useAtlasSelection, useAtlasSelectionSync } from '../DocsArea/Atlas/hooks/useAtlasSelection';
import AtlasToolbar from '../DocsArea/Atlas/components/AtlasToolbar';
import AtlasMinimap from '../DocsArea/Atlas/components/AtlasMinimap';
import AtlasWorld from '../DocsArea/Atlas/components/AtlasWorld';
import AtlasEmpty from '../DocsArea/Atlas/components/AtlasEmpty';
import { useCampaignsAtlasTree } from './hooks/useCampaignsAtlasTree';
import CampaignsAtlasNode from './components/CampaignsAtlasNode';
import CampaignsAtlasPeek from './components/CampaignsAtlasPeek';
import CampaignsAtlasContextMenu from './components/CampaignsAtlasContextMenu';
import '../DocsArea/Atlas/Atlas.css';

const CampaignPlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

const noop = () => {};
const noopFalse = () => false;
const EMPTY_IDS = new Set();

/**
 * Atlas — pan/zoom family-tree map of campaigns.
 */
const CampaignsAtlas = ({
  sprints = [],
  sprintOrder = [],
  onSelectSprint,
  onOpenInNewTab,
  onRenameSprint,
  onTrashSprint,
  openCreateSprintModal,
  openCreateNestedSprintModal,
}) => {
  const containerRef = useRef(null);
  const nodesRef = useRef([]);
  const didFolderDragRef = useRef(false);
  const minimapApplyRef = useRef(null);
  const {
    pan,
    zoom,
    panRef,
    zoomRef,
    cameraRef,
    gridRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    setPanLive,
    endPanLive,
    fitToBounds,
    resetView,
    didPanRef,
  } = useAtlasViewport({ containerRef, minimapApplyRef });

  const [nodeHeights, setNodeHeights] = useState(() => new Map());
  const [viewSize, setViewSize] = useState({ w: 0, h: 0 });
  const [renamingId, setRenamingId] = useState(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const apply = () => setViewSize({ w: el.clientWidth, h: el.clientHeight });
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { expandedId, setExpandedId, ctx, setCtx, selectedIds, setSelectedIds } =
    useAtlasSelection();

  const { nodes, edges, bounds, clusters, peek, treeReady } = useCampaignsAtlasTree({
    sprints,
    sprintOrder,
    expandedId,
    fitToBounds,
    nodeHeights,
  });
  nodesRef.current = nodes;
  useAtlasSelectionSync(nodes, expandedId, setExpandedId, setSelectedIds);

  const handleMeasuredHeights = useCallback((next) => {
    setNodeHeights((prev) => {
      if (!next || next.size === 0) return prev;
      if (prev.size !== next.size) return next;
      for (const [id, h] of next) {
        if (Math.abs((prev.get(id) || 0) - h) > 2) return next;
      }
      return prev;
    });
  }, []);

  const { registerNode, registerEdge, livePos } = useAtlasNodeMotion({
    layoutNodes: nodes,
    edges,
    onMeasuredHeights: handleMeasuredHeights,
    zoomRef,
  });

  const screenToWorld = useCallback((clientX, clientY) => {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    const p = panRef.current;
    const z = zoomRef.current || 1;
    return {
      x: (clientX - rect.left - p.x) / z,
      y: (clientY - rect.top - p.y) / z,
    };
  }, [panRef, zoomRef]);

  const {
    selectionBox,
    didMarqueeDragRef,
    startMarquee,
    moveMarquee,
    endMarquee,
  } = useAtlasMarquee({
    containerRef,
    nodesRef,
    screenToWorld,
    setSelectedIds,
    setCtx,
  });

  const {
    handlePointerDownCanvas,
    handlePointerMoveCanvas,
    handlePointerUpCanvas,
    handleNodeClick,
    handleCanvasClick,
    handleNodeContextMenu,
    handleCanvasContextMenu,
  } = useAtlasCanvasEvents({
    onPointerDown,
    onPointerMove,
    onPointerUp,
    startMarquee,
    moveMarquee,
    endMarquee,
    moveFolderPointer: noopFalse,
    endFolderPointer: noopFalse,
    didPanRef,
    didMarqueeDragRef,
    didFolderDragRef,
    setExpandedId,
    setSelectedIds,
    setCtx,
  });

  const sprintById = useCallback((id) => (
    nodes.find((n) => n.id === id)?.sprint || sprints.find((s) => s.id === id) || { id }
  ), [nodes, sprints]);

  const handleOpen = useCallback((id) => {
    if (!id) return;
    onSelectSprint?.(sprintById(id));
  }, [onSelectSprint, sprintById]);

  const handleOpenInNewTab = useCallback((id) => {
    if (!id) return;
    onOpenInNewTab?.(sprintById(id), 'campaign');
  }, [onOpenInNewTab, sprintById]);

  const handleCreateRoot = useCallback(() => {
    openCreateSprintModal?.();
  }, [openCreateSprintModal]);

  const handleCreateNested = useCallback((parentId) => {
    openCreateNestedSprintModal?.(parentId);
  }, [openCreateNestedSprintModal]);

  const handleRename = useCallback((id) => {
    setRenamingId(id);
    setCtx(null);
  }, [setCtx]);

  const handleRenameCommit = useCallback((id, name) => {
    if (id && name) onRenameSprint?.(id, name);
    setRenamingId(null);
  }, [onRenameSprint]);

  const handleTrash = useCallback((id) => {
    if (id) onTrashSprint?.(id);
  }, [onTrashSprint]);

  const renderNode = useCallback((props) => (
    <CampaignsAtlasNode
      {...props}
      onOpen={handleOpen}
      onOpenInNewTab={handleOpenInNewTab}
    />
  ), [handleOpen, handleOpenInNewTab]);

  const renderPeek = useCallback((props) => (
    <CampaignsAtlasPeek
      {...props}
      onOpen={handleOpen}
      onOpenInNewTab={handleOpenInNewTab}
    />
  ), [handleOpen, handleOpenInNewTab]);

  return (
    <div className="atlas">
      <AtlasToolbar
        bounds={bounds}
        fitToBounds={(b, pad) => fitToBounds(b, pad, { animate: true })}
        resetView={() => resetView(bounds, { animate: true })}
        showCreate
        createLabel="Campaign"
        createTitle="Create a campaign"
        createIcon={<CampaignPlusIcon />}
        fitTitle="Fit campaigns in view"
        resetTitle="Reset zoom and frame campaigns"
        hint="Scroll to zoom · drag to pan · click a campaign to peek · double-click to open"
        onCreateRootFolder={handleCreateRoot}
      />

      <div
        ref={containerRef}
        className={`atlas__canvas${selectionBox ? ' atlas__canvas--selecting' : ''}`}
        onPointerDown={handlePointerDownCanvas}
        onPointerMove={handlePointerMoveCanvas}
        onPointerUp={handlePointerUpCanvas}
        onPointerCancel={handlePointerUpCanvas}
        onClick={handleCanvasClick}
        onContextMenu={handleCanvasContextMenu}
      >
        <AtlasWorld
          viewW={viewSize.w}
          viewH={viewSize.h}
          pan={pan}
          zoom={zoom}
          cameraRef={cameraRef}
          gridRef={gridRef}
          nodes={nodes}
          edges={edges}
          clusters={clusters}
          peek={peek}
          draggingIds={EMPTY_IDS}
          livePos={livePos}
          expandedId={expandedId}
          selectedIds={selectedIds}
          dropHint={null}
          dropTargetId={null}
          registerNode={registerNode}
          registerEdge={registerEdge}
          onFolderPointerDown={noop}
          onDragOverFolder={noop}
          onDragLeaveFolder={noop}
          onDropOnFolder={noop}
          onNodeClick={handleNodeClick}
          onNodeContextMenu={handleNodeContextMenu}
          renamingId={renamingId}
          onRenameCommit={handleRenameCommit}
          onRenameCancel={() => setRenamingId(null)}
          renderNode={renderNode}
          renderPeek={renderPeek}
        />

        {selectionBox && (
          <div
            className="atlas__selection-box"
            aria-hidden
            style={{
              left: selectionBox.x,
              top: selectionBox.y,
              width: selectionBox.width,
              height: selectionBox.height,
            }}
          />
        )}

        {treeReady && nodes.length === 0 && (
          <AtlasEmpty
            title="No campaigns yet"
            body="This is the map of your campaigns. Nested campaigns sit under their parent, the same way folders do in Documents."
            actionLabel="Create a campaign"
            showAction
            onCreateRootFolder={handleCreateRoot}
          />
        )}
      </div>

      <AtlasMinimap
        nodes={nodes}
        edges={edges}
        bounds={bounds}
        pan={pan}
        zoom={zoom}
        containerRef={containerRef}
        liveApplyRef={minimapApplyRef}
        onPanChange={setPanLive}
        onPanChangeEnd={endPanLive}
      />

      <CampaignsAtlasContextMenu
        ctx={ctx}
        onCreateRoot={handleCreateRoot}
        onCreateNested={handleCreateNested}
        onOpen={handleOpen}
        onOpenInNewTab={handleOpenInNewTab}
        onRename={handleRename}
        onTrash={handleTrash}
        onClose={() => setCtx(null)}
      />
    </div>
  );
};

export default CampaignsAtlas;
